import { css, Theme } from '@emotion/react';
import { device } from './devices';
import { responseFont } from './mixins';

export interface LandsbankinnTheme {
  colors: {
    primary: string;
    secondary: string;
    ternary: string;
    foreground: string;
    background: string;
    backgroundAlt: string;
    focus: string;
    negative: string;
    positive: string;
    warning: string;
  };
  shades: {
    primary1: string;
    primary2: string;
    primary3: string;
    primary4: string;
    primary5: string;
    secondary1: string;
    secondary2: string;
    secondary3: string;
    secondary4: string;
    secondary5: string;
    ternary1: string;
    ternary2: string;
    ternary3: string;
    ternary4: string;
    ternary5: string;
  };
  gradients: {
    primary: string;
    secondary: string;
    aukakronur: string;
  };
  font: {
    family: string;
    familySerif: string;
    familySerifCond: string;
    familySlab: string;
  };
  graphs?: string[];
  gutter: number;
  segmentPadding: number;
  gridColumnCount: number;
  baseline: number;
  pageWidth: number;
  pageLimit: any;
  headerHeight: number;
  headerHeightMobile: number;
  useGridOverlay: boolean;
  containerGutter: number;
  breakpoints: {
    sm: { width: number; gutter: number };
    md: { width: number; gutter: number };
    ipadPro: { width: number; gutter: number };
    lg: { width: number; gutter: number };
  };
}

interface EmotionProps {
  theme: LandsbankinnTheme;
}

export const sizes = {
  /**18*/
  bodySize: 18,
  /**18*/
  bodySizeMin: 18,
  /**24*/
  bodyLineHeight: 24,
  /**14*/
  bodySmallSize: 14,
  /**13*/
  bodySmallSizeMin: 13,
  /**20*/
  bodySmallLineHeight: 20,
  /**14*/
  bodySmallBoldSize: 20,
  /**13*/
  bodySmallBoldSizeMin: 20,
  /**20*/
  bodySmallBoldLineHeight: 26,
  /**22*/
  bodyEditorialSize: 22,
  /**18*/
  bodyEditorialSizeMin: 18,
  /**32*/
  bodyEditorialLineHeight: 32,
  /**134*/
  displaySize: 134,
  /**80*/
  displaySizeMin: 80,
  /**154*/
  displayLineHeihght: 154,
  /**72*/
  heroSmallSize: 72,
  /**32*/
  heroSmallSizeMin: 32,
  /**88*/
  heroSmallLineHeight: 88,
  /**84*/
  h1Size: 84,
  /**40*/
  h1SizeMin: 40,
  /**104*/
  h1LineHeight: 104,
  /**56*/
  h2BigSize: 56,
  /**32*/
  h2BigSizeMin: 32,
  /**70*/
  h2BigLineHeight: 70,
  /**44*/
  h2Size: 44,
  /**30*/
  h2SizeMin: 30,
  /**54*/
  h2LineHeight: 54,
  /**28*/
  h3Size: 28,
  /**26*/
  h3SizeMin: 26,
  /**36*/
  h3LineHeight: 36,
  /**22*/
  h4Size: 22,
  /**20*/
  h4SizeMin: 20,
  /**32*/
  h4LineHeight: 32,
  /**18*/
  h5Size: 18,
  /**16*/
  h5SizeMin: 16,
  /**24*/
  h5LineHeight: 24,
  /**32*/
  p1Size: 32,
  /**28*/
  p1SizeMin: 28,
  /**40*/
  p1LineHeight: 40,
  /**24*/
  p2Size: 24,
  /**20*/
  p2SizeMin: 20,
  /**32*/
  p2LineHeight: 32,
  /**16*/
  numbersSize: 16,
  /**14*/
  numbersSizeMin: 14,
  /**24*/
  numbersLineHeight: 24,
  /**30*/
  numbers2Size: 18,
  /**26*/
  numbers2SizeMin: 18,
  /**40*/
  numbers2LineHeight: 24,
  /**24 */
  numbers2MSize: 24,
  /**26*/
  numbers2MSizeMin: 20,
  /**40*/
  numbers2MLineHeight: 40,
  /**40*/
  numbers4BigSize: 40,
  /**32*/
  numbers4BigSizeMin: 32,
  /**70*/
  numbers4BigLineHeight: 70,
  /**40*/
  numbersMSize: 40,
  /**32*/
  numbersMSizeMin: 32,
  /**48*/
  numbersMLineHeight: 48,
  /**60*/
  numbersBigSize: 60,
  /**42*/
  numbersBigSizeMin: 42,
  /**70*/
  numbersBigLineHeight: 70,
  /**84*/
  numbersBiggestSize: 84,
  /**52*/
  numbersBiggestSizeMin: 52,
  /**104*/
  numbersBiggestLineHeight: 104,
  /**16*/
  numbersTableSize: 16,
  /**14**/
  numbersTableSizeMin: 16,
  /**40*/
  numbersTableLineHeight: 24,
  /**16*/
  chartsSize: 16,
  /**14*/
  chartsSizeMin: 14,
  /**24*/
  chartsLineHeight: 24,
  /**24*/
  inputSize: 24,
  /**20*/
  inputSizeMin: 20,
  /**32*/
  inputSizeHeight: 32,
  /**28*/
  input2Size: 22,
  /**20*/
  input2SizeMin: 20,
  /**32*/
  input2SizeHeight: 28,
  /**14*/
  labelSize: 12,
  /**12*/
  labelSizeMin: 10,
  /**20*/
  labelLineHeight: 20,
};

export const body = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  font-weight: normal;

  ${responseFont(sizes.bodySizeMin, sizes.bodySize)(props)}

  line-height: ${sizes.bodyLineHeight / sizes.bodySize};
  letter-spacing: 0.28px;
`;

export const bodySmall = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  font-weight: normal;
  ${responseFont(sizes.bodySmallSizeMin, sizes.bodySmallSize)(props)}

  line-height: ${sizes.bodySmallLineHeight / sizes.bodySmallSize};
  letter-spacing: 0.2px;
`;

export const bodySmallBold = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  font-weight: 500;
  ${responseFont(sizes.bodySmallBoldSizeMin, sizes.bodySmallBoldSize)(props)}

  line-height: ${sizes.bodySmallBoldLineHeight / sizes.bodySmallBoldSize};
  letter-spacing: 0.36px;
`;

export const bodyEditorial = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerif};
  font-weight: normal;
  ${responseFont(sizes.bodyEditorialSizeMin, sizes.bodyEditorialSize)(props)}

  line-height: ${sizes.bodyEditorialLineHeight / sizes.bodyEditorialSize};
  letter-spacing: 0.25px;
`;

export const display = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;

  ${responseFont(sizes.displaySizeMin, sizes.displaySize)(props)}

  line-height: ${sizes.displayLineHeihght / sizes.displaySize};
  letter-spacing: 0.5px;
`;

export const heroSmall = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;

  ${responseFont(sizes.heroSmallSizeMin, sizes.heroSmallSize)(props)}

  line-height: ${sizes.heroSmallLineHeight / sizes.heroSmallSize};
  letter-spacing: 0.5px;
`;

export const h1 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;

  ${responseFont(sizes.h1SizeMin, sizes.h1Size)(props)}

  line-height: ${sizes.h1LineHeight / sizes.h1Size};
  letter-spacing: 0;
`;

export const h2Big = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;
  ${responseFont(sizes.h2BigSizeMin, sizes.h2BigSize)(props)}

  line-height: ${sizes.h2BigLineHeight / sizes.h2BigSize};
  letter-spacing: 0.5px;
`;

export const h2 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;
  ${responseFont(sizes.h2SizeMin, sizes.h2Size)(props)}

  line-height: ${sizes.h2LineHeight / sizes.h2Size};
  letter-spacing: 0.5px;
`;

export const h3 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;
  ${responseFont(sizes.h3SizeMin, sizes.h3Size)(props)}

  line-height: ${sizes.h3LineHeight / sizes.h3Size};
  letter-spacing: 0.5px;
`;

export const h4 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;
  ${responseFont(sizes.h4SizeMin, sizes.h4Size)(props)}

  line-height: ${sizes.h4LineHeight / sizes.h4Size};
  letter-spacing: 0.5px;
`;

export const h5 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySerifCond};
  font-weight: 500;
  ${responseFont(sizes.h5SizeMin, sizes.h5Size)(props)}

  line-height: ${sizes.h5LineHeight / sizes.h5Size};
  letter-spacing: 0.5px;
`;

export const p1 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  font-weight: normal;
  ${responseFont(sizes.p1SizeMin, sizes.p1Size)(props)}

  line-height: ${sizes.p1LineHeight / sizes.p1Size};
  letter-spacing: 0.25px;
`;

export const p2 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  font-weight: normal;
  ${responseFont(sizes.p2SizeMin, sizes.p2Size)(props)}

  line-height: ${sizes.p2LineHeight / sizes.p2Size};
  letter-spacing: 0.25px;
`;

export const numbers = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySlab};
  font-weight: 500;
  ${responseFont(sizes.numbersSizeMin, sizes.numbersSize)(props)}

  line-height: ${sizes.numbersLineHeight / sizes.numbersSize};
  letter-spacing: 0;
`;

export const numbers2 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  ${responseFont(sizes.numbers2SizeMin, sizes.numbers2Size)(props)}

  line-height: ${sizes.numbers2LineHeight / sizes.numbers2Size};
  letter-spacing: 0.2px;
`;

export const numbers4Big = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySlab};
  font-weight: 500;
  ${responseFont(sizes.numbers4BigSizeMin, sizes.numbers4BigSize)(props)}

  line-height: ${sizes.numbers4BigLineHeight / sizes.numbers4BigSize};
  letter-spacing: -0.6px;
`;

export const numbersMedium = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySlab};
  font-weight: bold;
  ${responseFont(sizes.numbersMSizeMin, sizes.numbersMSize)(props)}

  line-height: ${sizes.numbersMLineHeight / sizes.numbersMSize};
  letter-spacing: 0;
`;

export const numbers2Medium = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySlab};
  font-weight: 500;
  ${responseFont(sizes.numbers2MSizeMin, sizes.numbers2MSize)(props)}

  line-height: ${sizes.numbers2MLineHeight / sizes.numbers2MSize};
  letter-spacing: 0.2px;
`;

export const numbersBig = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySlab};
  font-weight: bold;
  ${responseFont(sizes.numbersBigSizeMin, sizes.numbersBigSize)(props)}

  line-height: ${sizes.numbersBigLineHeight / sizes.numbersBigSize};
  letter-spacing: 0;
`;

export const numbersBiggest = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySlab};
  font-weight: bold;
  ${responseFont(sizes.numbersBiggestSizeMin, sizes.numbersBiggestSize)(props)}

  line-height: ${sizes.numbersBiggestLineHeight / sizes.numbersBiggestSize};
  letter-spacing: 0;
`;

export const tableNumbers = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  ${responseFont(sizes.numbersTableSize, sizes.numbersTableSize)(props)}

  line-height: ${sizes.numbersTableLineHeight / sizes.numbersTableSize};
  letter-spacing: 0;
`;

export const charts = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  font-weight: 500;
  ${responseFont(sizes.chartsSizeMin, sizes.chartsSize)(props)}

  line-height: ${sizes.chartsLineHeight / sizes.chartsSize};
  letter-spacing: 0;
`;

export const input = (props: EmotionProps) => css`
  font-family: ${props.theme.font.familySlab};
  font-weight: 500;
  ${responseFont(sizes.inputSizeMin, sizes.inputSize)(props)}

  line-height: ${sizes.inputSizeHeight / sizes.inputSize};
  letter-spacing: 0.5px;
  color: rgba(21, 50, 76, 1);
`;

export const input2 = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  ${responseFont(sizes.input2SizeMin, sizes.input2Size)(props)}
  font-weight: 500;
  letter-spacing: 0.58px;
  color: rgb(22, 62, 99);
  line-height: ${sizes.input2SizeHeight / sizes.input2Size};
`;

export const label = (props: EmotionProps) => css`
  font-family: ${props.theme.font.family};
  font-weight: normal;
  ${responseFont(sizes.labelSizeMin, sizes.labelSize)(props)}

  line-height: ${sizes.labelLineHeight / sizes.labelSize};
  letter-spacing: 0.2px;
`;

export const listStyle = (props: EmotionProps) => css`
  position: relative;
  padding-left: 30px;
  margin-bottom: 28px;

  @media ${device.tablet} {
    padding-left: 40px;
  }

  &:before {
    content: '';
    display: inline-block;

    position: absolute;
    top: 6px;
    left: 0;

    margin-right: 20px;

    height: 8px;
    width: 8px;

    border-radius: 50%;

    background: currentColor;

    @media ${device.tablet} {
      left: 10px;
    }
  }
`;

export const heroListStyle = (props: EmotionProps) => css`
  ${responseFont(sizes.bodySizeMin, sizes.bodySize)(props)}
  line-height: ${sizes.bodyEditorialLineHeight / sizes.bodySize};

  position: relative;
  padding-left: 30px;
  margin-bottom: 5px;

  @media ${device.tablet} {
    padding-left: 40px;
  }

  &:before {
    content: '';
    display: inline-block;

    position: absolute;
    top: 12px;
    left: 0;

    margin-right: 20px;

    height: 2px;
    width: 11px;

    background: currentColor;

    @media ${device.tablet} {
      left: 10px;
    }
  }
`;

export const olListStyle = (props: EmotionProps) => css`
  position: relative;

  padding-left: 30px;
  margin-bottom: 28px;

  @media ${device.tablet} {
    padding-left: 0px;
  }

  &:before {
    content: counter(custom-counter) '. ';
    display: inline-block;
    position: relative;
    top: 0;
    left: 0;
    font-weight: bold;

    margin-right: 20px;

    @media ${device.tablet} {
      left: 10px;
    }
  }
`;

export const typography = {
  body,
  bodySmall,
  bodySmallBold,
  bodyEditorial,
  display,
  heroSmall,
  h1,
  h2Big,
  h2,
  h3,
  h4,
  h5,
  p1,
  p2,
  numbers,
  numbers2,
  numbers2Medium,
  numbers4Big,
  numbersMedium,
  numbersBig,
  numbersBiggest,
  tableNumbers,
  listStyle,
  heroListStyle,
  input,
  input2,
  label,
  olListStyle,
  charts,
  sizes,
};
